import {getGameEventV3, SUIT} from '@constant/game';
import {BestCombination, Currency, PlayerComponent} from '@interface';
import {TFunction} from 'i18next';
import {JoinedRoomProps} from 'poker-interfaces/game-room/game-room-interface';
import {CardObj, GameType} from 'poker-interfaces/gameLogic/gameLogicInterface';
import SystemStore from './mobx/system';
import {useLockFn, useThrottleFn} from 'ahooks';
import router from 'next/router';
import {isNil} from 'lodash-es';
import {notification} from 'antd';

export const getFailRedirectUrl = (): string => {
  return '/home';
  // const url = window?.localStorage.getItem('gatewayUrl');
  // return url || '/login';
};

const routesToBeRedirected = ['/game-recharge', '/game-review'];

const routesNotShowInitProfileSetting = ['/fortune-wheel', '/w'];

export const shouldRedirect = (pathname: string) => {
  return routesToBeRedirected.includes(pathname);
};

export const shouldNotShowInitProfileSetting = (pathname: string) => {
  return routesNotShowInitProfileSetting.includes(pathname);
};

export const getSafe = (fn, defaultValue) => {
  try {
    return fn();
  } catch {
    return defaultValue;
  }
};

export const getPlayerCurrentMoney = (playerObj: PlayerComponent): number => {
  if (playerObj?.remainingMoney) return playerObj.remainingMoney;
  return 0;
};

export const getPlayerCurrentBet = (playerObj: PlayerComponent): number => {
  if (playerObj?.currentBet) return playerObj.currentBet;
  return 0;
};

export const getCumulativeOffset = (element: HTMLElement) => {
  if (!element) return null;
  const {top, left, right, bottom, width, height} = element?.getBoundingClientRect() || {};

  return {
    top,
    left,
    right,
    bottom,
    width,
    height,
  };
};

export const getElementInfo = (element: HTMLElement) => {
  const temp1 = element.getBoundingClientRect();
  console.log('temp1', temp1);

  return temp1;
};

export const getPokerImage = ({suit, rank: value, isDesktop}: CardObj): string => {
  let suitString;
  if (suit === SUIT.SPADE) suitString = 'spade';
  else if (suit === SUIT.HEART) suitString = 'heart';
  else if (suit === SUIT.CLUB) suitString = 'club';
  else if (suit === SUIT.DIAMOND) suitString = 'diamond';

  let valueString;
  if (value > 10) {
    if (value === 11) valueString = 'J';
    else if (value === 12) valueString = 'Q';
    else if (value === 13) valueString = 'K';
    else if (value === 14) valueString = 'A';
  } else {
    valueString = value.toString();
  }
  return `/images-mobile/poker-card/normal_phase_2.1/${suitString}/${suitString}_${valueString}.webp`;
};

export const getPokerInsuranceImage = ({suit, rank: value}: CardObj): string => {
  if (isNil(suit) || isNil(value)) {
    return '';
  }

  let suitString;
  if (suit === SUIT.SPADE) suitString = 'spade';
  else if (suit === SUIT.HEART) suitString = 'heart';
  else if (suit === SUIT.CLUB) suitString = 'club';
  else if (suit === SUIT.DIAMOND) suitString = 'diamond';

  let valueString;
  if (value > 10) {
    if (value === 11) valueString = 'J';
    else if (value === 12) valueString = 'Q';
    else if (value === 13) valueString = 'K';
    else if (value === 14) valueString = 'A';
  } else {
    valueString = value.toString();
  }
  return `images-mobile/poker-card/insurance/${suitString}/${suitString}_${valueString}.svg`;
};

export const getPokerOutcardImage = ({suit, rank: value}: CardObj): string => {
  let suitString;
  if (suit === SUIT.SPADE) suitString = 'spade';
  else if (suit === SUIT.HEART) suitString = 'heart';
  else if (suit === SUIT.CLUB) suitString = 'club';
  else if (suit === SUIT.DIAMOND) suitString = 'diamond';

  let valueString;
  if (value > 10) {
    if (value === 11) valueString = 'J';
    else if (value === 12) valueString = 'Q';
    else if (value === 13) valueString = 'K';
    else if (value === 14) valueString = 'A';
  } else {
    valueString = value.toString();
  }
  return `images-mobile/poker-card/outcard/${suitString}/${suitString}_${valueString}.svg`;
};

export const getPokerBack = () => {
  return '/images-mobile/poker-card/card_back_phase_2.1.webp';
};

export const getPokerValueAsText = (value: number) => {
  if (value < 10) return value;
  if (value === 10) return 'T';
  if (value === 11) return 'J';
  if (value === 12) return 'Q';
  if (value === 13) return 'K';
  return 'A';
};

export const getBestCombinationText = (t: any, bestCombination: BestCombination) => {
  if (!bestCombination?.combinationName) return null;
  const name = bestCombination.combinationName;
  const displayValue = Array.isArray(bestCombination.displayValue)
    ? bestCombination.displayValue.map(val => getPokerValueAsText(val))
    : getPokerValueAsText(bestCombination.displayValue);

  if (!displayValue) return '';

  if (name === 'HI_CARD') return t('best_combination:hi_card', {value: displayValue});
  if (name === 'PAIR' || name === 'ONE_PAIR')
    return t('best_combination:one_pair', {value: displayValue});
  if (name === 'TWO_PAIRS')
    return t('best_combination:two_pairs', {
      firstPair: displayValue[0],
      secondPair: displayValue[1],
    });
  if (name === 'THREE_OF_A_KIND') return t('best_combination:set', {value: displayValue});
  if (name === 'STRAIGHT')
    return t('best_combination:straight', {fromValue: displayValue[1], toValue: displayValue[0]});
  if (name === 'FLUSH') return t('best_combination:flush', {value: displayValue});
  if (name === 'FULL_HOUSE')
    return t('best_combination:fullHouse', {
      three_cards: displayValue[0],
      two_cards: displayValue[1],
    });
  if (name === 'FOUR_OF_A_KIND') return t('best_combination:quad', {value: displayValue});
  if (name === 'STRAIGHT_FLUSH')
    return t('best_combination:straight_flush', {
      fromValue: displayValue[1],
      toValue: displayValue[0],
    });
  if (name === 'ROYAL_FLUSH') return t('best_combination:royal_flush');
  return name;
};

export const sortEmojiOrder = (a, b) => {
  const emojiOne = a.diamond > 0 ? 0 : 1;
  const emojiTwo = b.diamond > 0 ? 0 : 1;
  return emojiOne - emojiTwo;
};

export const compactFormatter = value => {
  if (value > 0) {
    const mult = 10 ** (3 - Math.floor(Math.log(value) / Math.LN10) - 1);
    const trimToThreeSigValue = Math.floor(value * mult) / mult;

    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      maximumSignificantDigits: 3,
    }).format(trimToThreeSigValue);
  }
  return 0;
};

export const debounce = (fn: Function, wait: number) => {
  let timeout;
  return function executedFunction(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
};

export const validateCanJoinAnyRooms = (joinedRooms: JoinedRoomProps[], system): boolean => {
  if (joinedRooms.length >= 4) {
    system.showModal('Cannot join more than 4 rooms!');
    return false;
  }
  return true;
};

export const showSystemError = (errorCode: string, t: TFunction, system: SystemStore): void => {
  system.showModal(t(`errorCodes:${errorCode}`));
};


export const handleJoinRoom = async (
  user: any,
  system: any,
  socket: any,
  data: {
    roomCode: string;
    isAllIn: boolean;
    isObserver?: boolean;
    isTournament?: boolean;
    isForceJoinTable?: boolean;
  },
  t: TFunction
) => {
  const {REQUEST_ROOM_ACCESS} = getGameEventV3();
  const ROOM_ACCESS_TIMEOUT = 400;
  const {roomCode, isAllIn, isObserver, isForceJoinTable, isTournament} = data || {};

  const isForceJoinTableInTournament = isForceJoinTable && isTournament;

  // Comprehensive authentication flow logging
  console.log('[handleJoinRoom] Starting room join process');
  console.log('[handleJoinRoom] Room code:', roomCode, 'Observer:', isObserver);
  console.log('[handleJoinRoom] Full data object:', JSON.stringify(data));
  console.log('[handleJoinRoom] Socket connection status:', socket?.isConnected);
  console.log('[handleJoinRoom] Socket authentication status:', socket?.isAuthenticated);
  console.log('[handleJoinRoom] Socket ready for room operations:', socket?.isReadyForRoomOperations);
  console.log('[handleJoinRoom] User token exists:', !!user?.getPlayerToken());
  console.log('[handleJoinRoom] User display name:', user?.getPlayerDisplayName());

  await new Promise(resolve => {
    setTimeout(() => resolve(null), ROOM_ACCESS_TIMEOUT);
  });

  try {
    console.log('[handleJoinRoom] Calling socket.call - this will wait for socket connection and authentication');
    const startTime = Date.now();
    
    const requestPayload = {
      roomCode,
      username: user?.getPlayerDisplayName() || '',
      token: user?.getPlayerToken(),
    };
    console.log('[handleJoinRoom] REQUEST_ROOM_ACCESS payload:', JSON.stringify(requestPayload));
    
    const response = (await socket.call(
      REQUEST_ROOM_ACCESS,
      requestPayload,
      {withNotification: false}
    )) as any;
    
    const endTime = Date.now();
    console.log('[handleJoinRoom] Socket.call completed in', endTime - startTime, 'ms');
    console.log('[handleJoinRoom] Response received:', response?.success ? 'SUCCESS' : 'FAILURE');
    console.log('[handleJoinRoom] Full response:', JSON.stringify(response));

    if (response?.success) {
      console.log('[handleJoinRoom] Successfully joined room, processing...');
      const info = await user.getPlayerInfo();

      system.disableTablesTransition();
      const index = info.joinedRoom.findIndex(room => room.roomCode === roomCode);

      if (index > -1) {
        console.log('[handleJoinRoom] User already in room, setting active table index:', index);
        system.setActiveTableIndex(index);
      } else {
        console.log('[handleJoinRoom] Adding new room to user\'s joined rooms');
        user.appendJoinedRooms({
          roomCode,
          roomType: response.data.roomType,
          gameType: response.data.gameType,
          currencyId: response.data.currentBet,
          isAllIn,
          maxBuyIn: response.data.maxBuyIn,
          minBuyIn: response.data.minBuyIn,
          smallBlind: response.data.smallBlind,
          bigBlind: response.data.bigBlind,
          actionTime: response.data.actionTime,
          isObserver,
        });

        if (!isTournament || isForceJoinTableInTournament) {
          const newIndex = user.joinedRooms.length - 1;
          console.log('[handleJoinRoom] Setting active table index to new room:', newIndex);
          system.setActiveTableIndex(newIndex);
        }
      }
      
      // Navigate to game room
      if (!isTournament || isForceJoinTableInTournament) {
        console.log('[handleJoinRoom] Navigating to game room:', roomCode);
        router.replace(
          {
            pathname: '/home',
            query: {currentRoom: roomCode},
          },
          undefined,
          {shallow: true}
        );
        system.showGameOverlayWithoutPush();
      }
      
      // So that transition won't be played immediately after switching pages
      await new Promise(resolve => {
        setTimeout(() => {
          system.enableTablesTransition();
          resolve(null);
        }, 1000);
      });

      console.log('[handleJoinRoom] Room join process completed successfully');
      return Promise.resolve(null);
    } else {
      console.log('[handleJoinRoom] Room access denied:', response);
      // Return an Error object to indicate failure
      return Promise.resolve(new Error(response?.message || 'Room access denied'));
    }
  } catch (err) {
    console.error('[handleJoinRoom] Error during room join process:', err);
    console.error('[handleJoinRoom] Error code:', err?.code);
    console.error('[handleJoinRoom] Error message:', err?.message);
    
    // Enhanced error logging for authentication issues
    if (err?.code === 'WS_0000' || err?.message?.includes('not connected')) {
      console.error('[handleJoinRoom] Socket authentication/connection error detected');
    }
    
    if (!isTournament) {
      // TODO: Add geolocation error handling
      // TooManyRooms modal sheet will handle the error. No need to show again
      if (!err.code) {
        console.error('[handleJoinRoom] No error code, showing generic socket error');
        showSystemError('WS_0000', t, system);
        return Promise.resolve(new Error(err?.message));
      }
      console.error('[handleJoinRoom] Showing system error for code:', err.code);
      showSystemError(err?.code, t, system);
    }
    return Promise.resolve(new Error(err?.message));
  }
};

export const handleJoinTournament = async (
  system: any,
  socket: any,
  data: {tournamentId: number},
  t: TFunction
) => {
  const {TOURNAMENT} = getGameEventV3();
  const {tournamentId} = data || {};
  const ROOM_ACCESS_TIMEOUT = 400;

  await new Promise(resolve => {
    setTimeout(() => resolve(null), ROOM_ACCESS_TIMEOUT);
  });

  try {
    const response = (await socket.call(
      TOURNAMENT.TOURNAMENT_EARLY_JOIN,
      {
        data: {tournamentId},
      },
      {withNotification: false}
    )) as any;

    if (response?.success) {
      console.log('response in handleJoinTournament()', response);
      return Promise.resolve(null);
    }
    return Promise.resolve(null);
  } catch (err) {
    console.log('err in handleJoinTournament()', err);
    if (!err.code) {
      showSystemError('WS_0000', t, system);
      return Promise.resolve(new Error(err?.message));
    }
    showSystemError(err?.code, t, system);
    return Promise.resolve(new Error(err?.message));
  }
};

export const areCardsEqual = (c1: CardObj, c2: CardObj): boolean => {
  if (!c1 || !c2) return false;
  return c1.rank === c2.rank && c1.suit === c2.suit;
};

export const areCardsArrayEqual = (arr1: CardObj[], arr2: CardObj[]): boolean => {
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) {
    return false;
  }
  for (let iii = 0; iii < arr1.length; iii += 1) {
    if (!areCardsEqual(arr1[iii], arr2[iii])) {
      return false;
    }
  }
  return true;
};

export function removeEmptyObjectProperties<T>(obj: T): T {
  // For form submission
  // TODO: Define better return type
  const o = Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v != null && v !== '' && typeof v !== 'undefined')
  );
  return o as T;
}

export const useThrottledAndLockedFn = ({
  fn,
  wait,
}: {
  fn: (...arg) => Promise<void>;
  wait: number;
}) => {
  // Add lock to an async function to prevent parallel executions.
  const lockedFn = useLockFn(fn);
  const {runAsync: throttledFn} = useThrottleFn(lockedFn, {wait});

  return {throttledFn};
};

export const arePropsEqualExcept = (prevProps, nextProps, except: string[] = []): boolean => {
  for (const [key, value] of Object.entries(nextProps)) {
    if (!except.includes(key)) {
      if (!prevProps[key]) return false;
      if (!Object.is(prevProps[key], value)) return false;
    }
  }
  return true;
};

export const getRelativePosition = (element, targetElement) => {
  const elementRect = element.getBoundingClientRect();
  const targetRect = targetElement.getBoundingClientRect();

  return {
    top: elementRect.top - targetRect.top,
    left: elementRect.left - targetRect.left,
    bottom: elementRect.bottom - targetRect.top,
    right: elementRect.right - targetRect.left,
    width: elementRect.width,
    height: elementRect.height,
    x: elementRect.x - targetRect.x,
    y: elementRect.y - targetRect.y,
  };
};

export const pause = durationMs => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(null);
    }, durationMs);
  });
};

export const trackCustomEvent = ({eventName, params}: {eventName; params?}) => {
  if (!window?.gtag) {
    throw new Error('gtag is not defined');
  }

  window.gtag('event', eventName, params);
};

export const clearLocalStorageExceptDontShowDisclaimerAgain = () => {
  if (!window?.localStorage) {
    return;
  }

  const keys = Object.keys(window.localStorage);

  // Iterate over the keys
  keys.forEach(key => {
    // Check if the key does not contain 'dontShowDisclaimerAgain' and 'hasAgreedDisclaimer'
    if (
      !key.includes('dontShowDisclaimerAgain') &&
      !key.includes('dontShowIPBlockAgain') &&
      !key.includes('referralCode') &&
      !key.includes('pendingInviteRoomCode') &&
      !key.includes('gatewayToken') &&
      !key.includes('pj_gateway') &&
      !key.includes('pj_merchant_open_id') &&
      !key.includes('pj_embedded')
    ) {
      // Remove the key from localStorage
      localStorage.removeItem(key);
    }
  });
};

export const formatCompactNumber = (num: number): string => {
  if (isNil(num)) return '0';

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
};

export const getOrdinal = (n: number) => {
  let ord = 'th';

  if (n % 10 === 1 && n % 100 !== 11) {
    ord = 'st';
  } else if (n % 10 === 2 && n % 100 !== 12) {
    ord = 'nd';
  } else if (n % 10 === 3 && n % 100 !== 13) {
    ord = 'rd';
  }

  return ord;
};

export const compactFormatterFourDigit = (value: number) => {
  if (value > 9999) {
    const mult = 10 ** (4 - Math.floor(Math.log(value) / Math.LN10) - 1);
    const trimToSigValue = Math.floor(value * mult) / mult;

    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      maximumSignificantDigits: 4,
    }).format(trimToSigValue);
  }
  return value;
};

export const roundUpToDecimal = (value, decimals = 3) => {
  return value
    ? (Math.ceil(parseFloat(value) * 10 ** decimals) / 10 ** decimals).toFixed(decimals)
    : '--';
};

export const copyTextToClipboard = async text => {
  if ('clipboard' in navigator) {
    // Use the Clipboard API
    return navigator.clipboard.writeText(text);
  }
  // Fallback for browsers that don't support the Clipboard API
  return document.execCommand('copy', true, text);
};
